import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, MessageCircle, Loader2 } from "lucide-react";
import { submitLead, waLink, WHATSAPP_MANAGUA, WHATSAPP_SJDS } from "@/lib/api";
import { SERVICES } from "@/data/site";
import { useLang } from "@/i18n/LangContext";

const field = "w-full bg-[#0B0F12] border border-[#00C9B7]/15 rounded-xl px-4 py-3.5 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-[#00C9B7] transition-colors";

const Pill = ({ active, onClick, children, testid, alert }) => (
  <button type="button" onClick={onClick} className={`px-4 py-2.5 rounded-full text-xs font-semibold border transition-colors ${active ? (alert ? "bg-rose-500 border-rose-500 text-white" : "bg-[#00C9B7] border-[#00C9B7] text-[#031312]") : "border-white/15 text-slate-300 hover:border-[#00C9B7]/60"}`} data-testid={testid}>{children}</button>
);

const Group = ({ label, children }) => (
  <div>
    <p className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-2.5">{label}</p>
    <div className="flex flex-wrap gap-2">{children}</div>
  </div>
);

export default function LeadForm({ source = "contact", location = "", showPhone = true, idPrefix = "lead", compact = false }) {
  const { tr, pick } = useLang();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "", pain: "", services: [], location });
  const toggle = (t) => setForm({ ...form, services: form.services.includes(t) ? form.services.filter((x) => x !== t) : [...form.services, t] });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.pain || !form.location) {
      toast.error(tr("Please tell us if you're in pain and your preferred location.", "Indícanos si tienes dolor y tu ubicación preferida."));
      return;
    }
    setBusy(true);
    try {
      await submitLead({ ...form, source });
      setDone(form);
      toast.success(tr("Message sent! We'll get you on our schedule.", "¡Mensaje enviado! Te daremos una cita pronto."));
    } catch {
      toast.error(tr("Please check your name and email and try again.", "Revisa tu nombre y correo e inténtalo de nuevo."));
    }
    setBusy(false);
  };

  if (done) {
    const parts = [
      tr(`Hello Dr. Cora! I'm ${done.name}.`, `¡Hola Dra. Cora! Soy ${done.name}.`),
      done.pain === "yes" ? tr("I'm in pain right now.", "Tengo dolor en este momento.") : "",
      done.services.length ? `${tr("Interested in", "Me interesa")}: ${done.services.join(", ")}.` : "",
      `${tr("Preferred location", "Ubicación preferida")}: ${done.location}.`,
      done.message,
    ];
    const text = parts.filter(Boolean).join(" ");
    const number = done.location === "Managua" ? WHATSAPP_MANAGUA : WHATSAPP_SJDS;
    return (
      <div className="text-center py-10" data-testid={`${idPrefix}-success`}>
        <CheckCircle2 className="mx-auto text-[#00C9B7] mb-4" size={48} />
        <p className="font-display text-2xl uppercase mb-2">{tr("Thank you", "Gracias")}, {done.name.split(" ")[0]}!</p>
        <p className="text-slate-400 mb-6">{tr("Your message has been received. Want a faster reply?", "Recibimos tu mensaje. ¿Quieres una respuesta más rápida?")}</p>
        <a href={waLink(text, number)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-[#25D366] text-white px-6 py-3 text-sm font-bold" data-testid={`${idPrefix}-whatsapp-followup`}><MessageCircle size={18} /> {tr("Also send on WhatsApp", "Enviar también por WhatsApp")}</a>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4" data-testid={`${idPrefix}-form`}>
      <div className={compact ? "space-y-4" : "grid sm:grid-cols-2 gap-4"}>
        <input required value={form.name} onChange={set("name")} placeholder={tr("Name (First, Last)*", "Nombre (Nombre, Apellido)*")} className={field} data-testid={`${idPrefix}-name-input`} />
        <input required type="email" value={form.email} onChange={set("email")} placeholder={tr("Email*", "Correo*")} className={field} data-testid={`${idPrefix}-email-input`} />
      </div>
      {showPhone && <input value={form.phone} onChange={set("phone")} placeholder={tr("Phone / WhatsApp", "Teléfono / WhatsApp")} className={field} data-testid={`${idPrefix}-phone-input`} />}
      <Group label={tr("Are you in pain right now?*", "¿Tienes dolor en este momento?*")}>
        <Pill alert active={form.pain === "yes"} onClick={() => setForm({ ...form, pain: "yes" })} testid={`${idPrefix}-pain-yes`}>{tr("Yes, I'm in pain", "Sí, tengo dolor")}</Pill>
        <Pill active={form.pain === "no"} onClick={() => setForm({ ...form, pain: "no" })} testid={`${idPrefix}-pain-no`}>{tr("No pain", "Sin dolor")}</Pill>
      </Group>
      <Group label={tr("Preferred location*", "Ubicación preferida*")}>
        {["San Juan del Sur", "Managua"].map((loc) => (
          <Pill key={loc} active={form.location === loc} onClick={() => setForm({ ...form, location: loc })} testid={`${idPrefix}-location-${loc === "Managua" ? "managua" : "sjds"}`}>{loc}</Pill>
        ))}
      </Group>
      <Group label={tr("Services you're interested in", "Servicios de interés")}>
        {SERVICES.map((s, i) => (
          <Pill key={s.title} active={form.services.includes(s.title)} onClick={() => toggle(s.title)} testid={`${idPrefix}-service-${i}`}>{pick(s, "title")}</Pill>
        ))}
      </Group>
      <textarea rows={compact ? 3 : 5} value={form.message} onChange={set("message")} placeholder={tr("What questions do you have & how can I help?", "¿Qué preguntas tienes y cómo puedo ayudarte?")} className={field} data-testid={`${idPrefix}-message-input`} />
      <button disabled={busy} className="w-full rounded-xl bg-[#00C9B7] text-[#031312] py-4 text-sm font-bold uppercase tracking-[0.2em] hover:bg-[#00E0CC] active:scale-[0.99] transition-[background-color,transform] disabled:opacity-60 inline-flex justify-center items-center gap-2" data-testid={`${idPrefix}-submit-btn`}>
        {busy && <Loader2 className="animate-spin" size={16} />} {tr("Send", "Enviar")}
      </button>
    </form>
  );
}
